import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, CardContent, Typography, FormControlLabel, Checkbox } from '@material-ui/core';
import { ICourseKLineConfig } from '../../../components/CourseKLineSelector';
import { IDialectOption } from 'components/selects/DialectSelect';
import MemoryTranslationFilter from './common/MemoryTranslationFilter';
import { TRANSLATION_REF_OPTIONS } from '../constants/translationRefOptions';

export type IPatternFrequencyConfig = {
  filtering: {
    courses: ICourseKLineConfig[];
    only_published_lessons?: boolean;
  };
  dialect: number | string;
  refs: number[];
};

type Props = {
  onGenerate: (config: IPatternFrequencyConfig) => void;
  className?: string;
};

const defaultConfig: IPatternFrequencyConfig = {
  filtering: { courses: [] },
  dialect: '',
  refs: TRANSLATION_REF_OPTIONS.map((o) => o.value),
};

const MemoryPatternFrequencyConfig: React.FC<Props> = ({ onGenerate, className }) => {
  const [config, setConfig] = useState<IPatternFrequencyConfig>(defaultConfig);

  const handleCoursesChange = useCallback((courses: ICourseKLineConfig[]) => {
    setConfig((prev) => ({ ...prev, filtering: { ...prev.filtering, courses } }));
  }, []);

  const handleDialectChange = useCallback((selected: IDialectOption) => {
    // Only source dialect is shown; send numeric ID for backend
    setConfig((prev) => ({ ...prev, dialect: selected.dialectId }));
  }, []);

  const handleRefsChange = useCallback((refs: number[]) => {
    setConfig((prev) => ({ ...prev, refs }));
  }, []);

  const handleOnlyPublishedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    setConfig((prev) => ({ ...prev, filtering: { ...prev.filtering, only_published_lessons: checked } }));
  }, []);

  const canGenerate = useMemo(() => {
    return (
      (config.dialect as number) > 0 && Array.isArray(config.filtering.courses) && config.filtering.courses.length > 0
    );
  }, [config]);

  const onClickGenerate = () => onGenerate(config);

  return (
    <Wrapper className={className}>
      <Header>
        <Title>Pattern Frequency</Title>
        <Description>Configure the report and generate a frequency table of normalized patterns.</Description>
      </Header>

      <FullWidth>
        <CardStyled>
          <CardContent>
            <MemoryTranslationFilter
              valueCourses={config.filtering.courses}
              onCoursesChange={handleCoursesChange}
              valueOnlyPublished={!!config.filtering.only_published_lessons}
              onOnlyPublishedChange={(checked) =>
                setConfig((p) => ({ ...p, filtering: { ...p.filtering, only_published_lessons: checked } }))
              }
              showDialect
              dialectId={typeof config.dialect === 'number' ? (config.dialect as number) : 0}
              onDialectChange={(id) => setConfig((p) => ({ ...p, dialect: id }))}
              showRefs
              valueRefs={config.refs}
              onRefsChange={handleRefsChange}
              refOptions={TRANSLATION_REF_OPTIONS}
            />
          </CardContent>
        </CardStyled>
      </FullWidth>

      <Actions>
        <Button variant="contained" color="primary" size="large" onClick={onClickGenerate} disabled={!canGenerate}>
          Generate Report
        </Button>
      </Actions>
    </Wrapper>
  );
};

export default MemoryPatternFrequencyConfig;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Title = styled(Typography).attrs({ variant: 'h5' })``;
const Description = styled(Typography).attrs({ variant: 'body2' })``;
const SectionTitle = styled(Typography).attrs({ variant: 'h6' })``;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
`;

const FullWidth = styled.div`
  margin-top: 16px;
`;

const CardStyled = styled(Card)`
  && {
    box-shadow: none;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;
